"use client";

import React, { useState, useRef, useEffect } from "react";
import { useIndicatorRegistry } from "../../lib/registry/IndicatorRegistry";

interface IndicatorDropdownProps {
  onToggleIndicator: (id: string, enabled: boolean) => void;
  enabledStates: Record<string, boolean>;
  allowedIndicatorIds?: string[]; // If provided, only show these indicators
  backtestableIndicatorIds?: string[];
  className?: string;
}

export const IndicatorSelector: React.FC<IndicatorDropdownProps> = ({
  onToggleIndicator,
  enabledStates,
  allowedIndicatorIds,
  backtestableIndicatorIds,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const registry = useIndicatorRegistry();

  // Get indicators from the registry
  const all = registry.getConfigs();
  const indicators =
    allowedIndicatorIds && allowedIndicatorIds.length > 0
      ? all.filter((cfg) => allowedIndicatorIds.includes(cfg.id))
      : all;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = (id: string) => {
    const currentEnabled = enabledStates[id] ?? false;
    onToggleIndicator(id, !currentEnabled);
  };

  const activeCount = indicators.filter(
    (indicator) => !!enabledStates[indicator.id]
  ).length;

  const backtestableSet = new Set(backtestableIndicatorIds ?? []);

  return (
    <div
      className={`relative w-full ${className ?? ""}`}
      ref={dropdownRef}
      style={{ zIndex: 999 }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full min-w-[12rem] h-10 px-4 bg-slate-800 text-white border border-gray-600 rounded-md hover:bg-slate-700 focus:outline-none"
      >
        <span>Indicators ({activeCount})</span>
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "transform rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-[1200] w-full min-w-[12rem] mt-2 bg-slate-800 border border-gray-600 rounded-md shadow-lg md:bottom-full md:mb-2 md:mt-0">
          {indicators.map((indicator) => (
            <label
              key={indicator.id}
              className="flex items-center px-4 py-2 hover:bg-slate-700 cursor-pointer"
              title={indicator.description}
            >
              <input
                type="checkbox"
                checked={!!enabledStates[indicator.id]}
                onChange={() => handleToggle(indicator.id)}
                className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
              />
              <span className="ml-2 text-white">
                {backtestableSet.has(indicator.id) && (
                  <span className="text-emerald-400 mr-1">·</span>
                )}
                {indicator.name}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
